import { styled } from 'styled-system/jsx'

const ManagerRoot = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '18px',
  },
})

const TopRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const Lede = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.5',
  },
})

const SlugRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const SlugInputRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'sm',
    backgroundColor: '#FFFFFF',
    paddingX: '12px',
    paddingY: '0',
    _focusWithin: {
      borderColor: 'quickpass.primary',
    },
  },
})

const SlugPrefix = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
  },
})

const SlugInput = styled('input', {
  base: {
    flex: '1',
    minWidth: '0',
    fontFamily: 'body',
    fontSize: '15px',
    paddingY: '12px',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'quickpass.ink',
    _placeholder: { color: '#B8B0CC', opacity: '1' },
  },
})

const SlugHint = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
  },
  variants: {
    tone: {
      idle: { color: 'quickpass.inkSoft' },
      checking: { color: 'quickpass.inkSoft' },
      ok: { color: '#10663A' },
      taken: { color: '#8B1E2C' },
      invalid: { color: '#8B1E2C' },
    },
  },
})

const TextField = styled('input', {
  base: {
    width: '100%',
    minWidth: '0',
    fontFamily: 'body',
    fontSize: '15px',
    paddingX: '12px',
    paddingY: '10px',
    borderRadius: 'sm',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    backgroundColor: '#FFFFFF',
    color: 'quickpass.ink',
    outline: 'none',
    _focus: { borderColor: 'quickpass.primary' },
    _placeholder: { color: '#B8B0CC', opacity: '1' },
  },
})

const TextArea = styled('textarea', {
  base: {
    width: '100%',
    minWidth: '0',
    minHeight: '92px',
    resize: 'vertical',
    fontFamily: 'body',
    fontSize: '14px',
    lineHeight: '1.5',
    paddingX: '12px',
    paddingY: '10px',
    borderRadius: 'sm',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    backgroundColor: '#FFFFFF',
    color: 'quickpass.ink',
    outline: 'none',
    _focus: { borderColor: 'quickpass.primary' },
    _placeholder: { color: '#B8B0CC', opacity: '1' },
  },
})

const FieldLabel = styled('label', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: 'quickpass.ink',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
})

const FieldStack = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const VisibilityList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: '#FAF7FF',
    borderRadius: 'sm',
    padding: '14px',
  },
})

const VisibilityRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
})

const VisibilityText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
})

const VisibilityName = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const VisibilityDescription = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    lineHeight: '1.4',
  },
})

const ShareLinkRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
})

const ShareLinkBox = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingX: '12px',
    paddingY: '10px',
    borderRadius: 'sm',
    backgroundColor: '#F1ECFF',
    border: '1.5px solid #E5E0F5',
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.ink',
    wordBreak: 'break-all',
  },
})

const ShareLinkActions = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
})

const PrimaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    cursor: 'pointer',
    _hover: { opacity: '0.92' },
    _disabled: { cursor: 'not-allowed', opacity: '0.5' },
  },
})

const SecondaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.primary',
    backgroundColor: 'transparent',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'pill',
    paddingX: '14px',
    paddingY: '8px',
    cursor: 'pointer',
    _hover: { borderColor: 'quickpass.primary' },
    _disabled: { cursor: 'not-allowed', opacity: '0.5' },
  },
})

const DangerLink = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: '#8B1E2C',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    paddingX: '4px',
    _hover: { textDecoration: 'underline' },
  },
})

const Banner = styled('div', {
  base: {
    paddingX: '14px',
    paddingY: '10px',
    borderRadius: 'sm',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
  },
  variants: {
    tone: {
      info: { backgroundColor: '#F1ECFF', color: 'quickpass.primary' },
      success: { backgroundColor: '#D7F5E1', color: '#10663A' },
      error: { backgroundColor: '#FFE3DD', color: '#8B1E2C' },
    },
  },
})

const StatsRow = styled('div', {
  base: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const InactiveBanner = styled('div', {
  base: {
    paddingX: '14px',
    paddingY: '10px',
    borderRadius: 'sm',
    backgroundColor: '#FFF1CC',
    color: '#7A5300',
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
  },
})

export {
  ManagerRoot,
  TopRow,
  Lede,
  SlugRow,
  SlugInputRow,
  SlugPrefix,
  SlugInput,
  SlugHint,
  TextField,
  TextArea,
  FieldLabel,
  FieldStack,
  VisibilityList,
  VisibilityRow,
  VisibilityText,
  VisibilityName,
  VisibilityDescription,
  ShareLinkRow,
  ShareLinkBox,
  ShareLinkActions,
  PrimaryButton,
  SecondaryButton,
  DangerLink,
  Banner,
  StatsRow,
  InactiveBanner,
}
